import * as yup from 'yup';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import type { ReminderDayType, TaskStatus } from '@/types/clientTasksApiTypes';
import type { Nullable } from '@/types/common';
import { formatMMDDYYYYToYYYYMMDD, parseDateAsLocal } from '@/utils/formatDate';
import { TASK_DETAILS_MAX_LENGTH } from '../../../../consts/client-detail';

export type TaskFormValues = {
  title: string;
  deadline: string;
  status: TaskStatus;
  agent_id: SelectorOption<string> | null;
  description: string;
  reminder_days: Nullable<ReminderDayType>;
};

const isDeadlineNotPast = (value: string | undefined) => {
  if (!value) return false;

  const yyyyMmDd = formatMMDDYYYYToYYYYMMDD(value);
  if (!yyyyMmDd) return false;

  const parsed = parseDateAsLocal(yyyyMmDd);
  if (Number.isNaN(parsed.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return parsed >= today;
};

export const taskFormValidationSchema = yup
  .object({
    title: yup.string().trim().required('Task title is required'),
    deadline: yup
      .string()
      .trim()
      .required('Deadline is required')
      .test('is-not-past', 'Deadline must be a future date', isDeadlineNotPast),
    status: yup.mixed<TaskStatus>().oneOf(['OPEN', 'COMPLETED', 'CLOSED']).required(),
    agent_id: yup.mixed<SelectorOption<string>>().nullable().defined(),
    description: yup
      .string()
      .defined()
      .max(TASK_DETAILS_MAX_LENGTH, `Task details must not exceed ${TASK_DETAILS_MAX_LENGTH} characters`),
    reminder_days: yup.mixed<ReminderDayType>().nullable().oneOf([1, 3, 10, null]).defined(),
  })
  .test('reminder-requires-assignee', function (values) {
    if (values.reminder_days != null && !values.agent_id?.value) {
      return this.createError({
        path: 'agent_id',
        message: 'Select a task assignee to use a reminder.',
      });
    }

    return true;
  });
